import { Monitor, Smartphone, Package, Code, Barcode, Terminal } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSystemLogo } from '../hooks/useSystemLogo';

export const SelectionPage = () => {
  const navigate = useNavigate();
  const { logoUrl, nomeSistema } = useSystemLogo();

  const handleSelectSimulation = (type: 'web' | 'flutter-android' | 'flutter-windows' | 'android-studio' | 'build-apk' | 'products-barcode') => {
    if (type === 'web') {
      navigate('/login');
    } else if (type === 'flutter-android') {
      // Emulador Android Flutter
      window.open('http://localhost:6080', '_blank');
    } else if (type === 'flutter-windows') {
      // Flutter Web/Windows/Linux
      window.open('http://localhost:8082', '_blank');
    } else if (type === 'android-studio') {
      // Android Studio VNC
      window.open('http://localhost:6081/vnc.html', '_blank');
    } else if (type === 'build-apk') {
      navigate('/build-apk');
    } else if (type === 'products-barcode') {
      navigate('/products-barcode');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 flex items-center justify-center p-4">
      <div className="w-full max-w-6xl">
        {/* Logo e Header */}
        <div className="text-center mb-12">
          {logoUrl && <img src={logoUrl} alt={nomeSistema} className="max-w-xs w-auto h-auto mx-auto mb-8" />}
          <h1 className="text-2xl font-bold text-gray-800 mb-2">
            {nomeSistema}
          </h1>
          <h2 className="text-lg font-semibold text-gray-600 mb-6">
            Gestão de Inventário
          </h2>
          <p className="text-sm text-gray-500">
            Acesse o sistema com suas credenciais
          </p>
        </div>

        {/* Cards de Seleção - Grid 3x2 com 5 cards */}
        <div className="grid md:grid-cols-3 gap-6">
          {/* Card 1: Sistema Online (Nuvem) */}
          <button
            onClick={() => handleSelectSimulation('web')}
            className="bg-white rounded-xl border-2 border-gray-200 shadow-lg p-8 hover:border-blue-700 hover:shadow-xl transition-all duration-300 group"
          >
            <div className="flex flex-col items-center text-center">
              <div className="w-20 h-20 bg-blue-50 rounded-lg flex items-center justify-center mb-6 group-hover:bg-blue-700 transition-colors duration-300">
                <Monitor className="w-10 h-10 text-blue-700 group-hover:text-white transition-colors duration-300" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">
                Sistema Online (Nuvem)
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Sistema completo de gestão e relatórios
              </p>
              <div className="mt-auto pt-4">
                <span className="text-blue-700 font-semibold group-hover:text-blue-800">
                  Acessar →
                </span>
              </div>
            </div>
          </button>

          {/* Card 2: Sistema Offline Flutter (Windows/Linux) */}
          <button
            onClick={() => handleSelectSimulation('flutter-windows')}
            className="bg-white rounded-xl border-2 border-gray-200 shadow-lg p-8 hover:border-purple-600 hover:shadow-xl transition-all duration-300 group"
          >
            <div className="flex flex-col items-center text-center">
              <div className="w-20 h-20 bg-purple-50 rounded-lg flex items-center justify-center mb-6 group-hover:bg-purple-600 transition-colors duration-300">
                <Code className="w-10 h-10 text-purple-600 group-hover:text-white transition-colors duration-300" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">
                Sistema Offline Flutter (Windows/Linux)
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Flutter Web para desktop
              </p>
              <div className="mt-auto pt-4">
                <span className="text-purple-600 font-semibold group-hover:text-purple-700">
                  Acessar →
                </span>
              </div>
            </div>
          </button>

          {/* Card 3: Sistema Offline Flutter (Celular Android) */}
          <button
            onClick={() => handleSelectSimulation('flutter-android')}
            className="bg-white rounded-xl border-2 border-gray-200 shadow-lg p-8 hover:border-green-600 hover:shadow-xl transition-all duration-300 group"
          >
            <div className="flex flex-col items-center text-center">
              <div className="w-20 h-20 bg-green-50 rounded-lg flex items-center justify-center mb-6 group-hover:bg-green-600 transition-colors duration-300">
                <Smartphone className="w-10 h-10 text-green-600 group-hover:text-white transition-colors duration-300" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">
                Sistema Offline Flutter (Celular Android)
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Emulador Android para contagem offline
              </p>
              <div className="mt-auto pt-4">
                <span className="text-green-600 font-semibold group-hover:text-green-700">
                  Acessar →
                </span>
              </div>
            </div>
          </button>

          {/* Card 4: Produtos / Código de Barras */}
          <button
            onClick={() => handleSelectSimulation('products-barcode')}
            className="bg-white rounded-xl border-2 border-gray-200 shadow-lg p-8 hover:border-amber-600 hover:shadow-xl transition-all duration-300 group"
          >
            <div className="flex flex-col items-center text-center">
              <div className="w-20 h-20 bg-amber-50 rounded-lg flex items-center justify-center mb-6 group-hover:bg-amber-600 transition-colors duration-300">
                <Barcode className="w-10 h-10 text-amber-600 group-hover:text-white transition-colors duration-300" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">
                Produtos / Código de Barras
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Visualizar produtos e gerar códigos de barras para testes
              </p>
              <div className="mt-auto pt-4">
                <span className="text-amber-600 font-semibold group-hover:text-amber-700">
                  Acessar →
                </span>
              </div>
            </div>
          </button>

          {/* Card 5: Android Studio */}
          <button
            onClick={() => handleSelectSimulation('android-studio')}
            className="bg-white rounded-xl border-2 border-gray-200 shadow-lg p-8 hover:border-cyan-600 hover:shadow-xl transition-all duration-300 group"
          >
            <div className="flex flex-col items-center text-center">
              <div className="w-20 h-20 bg-cyan-50 rounded-lg flex items-center justify-center mb-6 group-hover:bg-cyan-600 transition-colors duration-300">
                <Terminal className="w-10 h-10 text-cyan-600 group-hover:text-white transition-colors duration-300" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">
                Android Studio
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                IDE completo via VNC
              </p>
              <div className="mt-auto pt-4">
                <span className="text-cyan-600 font-semibold group-hover:text-cyan-700">
                  Acessar →
                </span>
              </div>
            </div>
          </button>


        </div>

        {/* Botão Gerar APK */}
        <div className="mt-6 flex justify-center">
          <button
            onClick={() => handleSelectSimulation('build-apk')}
            className="bg-white border-2 border-purple-600 text-purple-600 hover:bg-purple-50 px-6 py-3 rounded-lg font-semibold flex items-center gap-2 transition-all duration-300 shadow-md hover:shadow-lg"
          >
            <Package className="w-5 h-5" />
            <span>Gerar APKs (Flutter + React)</span>
          </button>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-xs text-gray-500 mb-2">
            © 2025 AuditLog. Todos os direitos reservados.
          </p>
        </div>
      </div>
    </div>
  );
};
